export class CartTieredGwp extends HTMLElement {
  static _swapInProgress = false;
  static _requestInFlight = false;
  static _globalListenersBound = false;
  static SHIPPING_UNLOCKED_ICON = `<span class="message--shipping-unlocked__icon" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="14" height="14" rx="7" fill="#000"/><path d="M10 5L5.875 9L4 7.18182" stroke="white" stroke-linecap="round" stroke-linejoin="round"/></svg></span>`;
  static _processing = false;
  static _declinedAutoGwpKeys = new Set();

  static _declineKey(thresholdAmount, productId) {
    return `${thresholdAmount}:${productId}`;
  }

  static _executeAutoAdd(items) {
    if (!items?.length || CartTieredGwp._processing) return;

    CartTieredGwp._processing = true;
    if (window.liquidAjaxCart?.add) {
      window.liquidAjaxCart.add(
        { items },
        {
          info: { initiator: 'cart-tiered-gwp' },
          lastCallback: () => {
            CartTieredGwp._processing = false;
          },
        }
      );
      return;
    }

    fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then(() => {
        CartTieredGwp._processing = false;
      })
      .catch(() => {
        CartTieredGwp._processing = false;
      });
  }

  static _finishRemoveRequest(pendingAutoAddItems) {
    CartTieredGwp._processing = false;
    CartTieredGwp._executeAutoAdd(pendingAutoAddItems);
  }

  static _bindGlobalListeners() {
    if (CartTieredGwp._globalListenersBound) return;
    CartTieredGwp._globalListenersBound = true;
    document.addEventListener('liquid-ajax-cart:request-start', () => {
      CartTieredGwp._requestInFlight = true;
    });
    document.addEventListener('liquid-ajax-cart:request-end', () => {
      CartTieredGwp._requestInFlight = false;
    });
  }

  constructor() {
    super();
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.lastCartItems = [];
    this.gwpVariantMap = {};
  }

  connectedCallback() {
    CartTieredGwp._bindGlobalListeners();

    this.productsEl = this.querySelector('[data-tiered-gwp-products]');
    this.messagesEl = document.getElementById('tiered-gwp-messages');
    this.currency = document.body.dataset.currency || 'USD';
    this.currencySymbol = window.settings?.currency_symbol || '$';
    this.isBFCM = !!window.settings?.enable_bfcm_promotion;

    document.addEventListener('liquid-ajax-cart:request-end', this.onCartUpdate);
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);

    this._onLineItemChangeVariant = this._handleLineItemChangeVariant.bind(this);
    this._onLineItemVariantClick = this._handleLineItemVariantClick.bind(this);
    document.addEventListener('click', this._onLineItemChangeVariant);
    document.addEventListener('click', this._onLineItemVariantClick);

    // During an active cart request, liquid-ajax-cart is moving this (static)
    // element mid-swap, which re-fires connectedCallback. Rendering here is
    // redundant with `onCartUpdate` (request-end) and churns the bar — it can
    // even capture a null `messagesEl` while the DOM is mid-swap, so the bar
    // appears to blink. Skip; `onCartUpdate` renders once on the settled DOM.
    // First ATC from an empty cart is different: this node did not exist yet,
    // so request-start never armed `_requestInFlight` / the bar is still empty
    // and must paint here (request-end may already have fired).
    const barIsEmpty = !this.messagesEl || !this.messagesEl.innerHTML.trim();
    if ((CartTieredGwp._swapInProgress || CartTieredGwp._requestInFlight) && !barIsEmpty) {
      return;
    }

    if (this.lastCartItems.length > 0) {
      this.render({
        items: this.lastCartItems,
        total_price: this.lastCartItems.reduce(
          (sum, item) => sum + (item.final_line_price || item.line_price || 0),
          0
        ),
      });
      return;
    }

    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
      .then((r) => r.json())
      .then((cart) => {
        if (!this.isConnected) return;
        this.lastCartItems = cart.items || [];
        this.render(cart);
      })
      .catch(() => {});
  }

  disconnectedCallback() {
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartUpdate);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    document.removeEventListener('click', this._onLineItemChangeVariant);
    document.removeEventListener('click', this._onLineItemVariantClick);
  }

  formatMoney(cents) {
    const raw = (Math.abs(cents) / 100).toFixed(2).replace(/\.00$/, '');
    return `${this.currencySymbol}${raw}`;
  }

  numberToWord(num) {
    const words = {
      1: 'ONE', 2: 'TWO', 3: 'THREE', 4: 'FOUR', 5: 'FIVE',
      6: 'SIX', 7: 'SEVEN', 8: 'EIGHT', 9: 'NINE', 10: 'TEN',
    };
    return words[num] || num.toString();
  }

  onCartUpdate(event) {
    if (CartTieredGwp._swapInProgress) return;

    const detail = event.detail || {};
    const { requestState } = detail;
    if (!requestState?.responseData?.ok) return;

    let cart = detail.cart;

    if (!cart || typeof cart.total_price === 'undefined') {
      const body = requestState.responseData.body;
      if (body && typeof body.total_price !== 'undefined' && Array.isArray(body.items)) {
        cart = body;
      }
    }

    if (cart && typeof cart.total_price !== 'undefined') {
      this._trackDeclinedAutoGwps(this.lastCartItems, cart.items || [], requestState);
      this.lastCartItems = cart.items || [];
      this.render(cart);
      return;
    }

    // Paint instantly from the last-known items + the just-added line so the bar
    // is visible the moment the drawer opens (no pop-in); the XHR read reconciles.
    const body = requestState.responseData.body || {};
    const addedItems = Array.isArray(body.items)
      ? body.items
      : (body.id != null || body.variant_id != null ? [body] : []);
    const optimisticItems = [...this.lastCartItems, ...addedItems];
    this.render({
      items: optimisticItems,
      total_price: optimisticItems.reduce(
        (sum, item) => sum + (item.final_line_price || item.line_price || item.price || 0),
        0
      ),
    });

    // Read the live cart with XMLHttpRequest, not fetch: the global window.fetch
    // patch serves /cart.js from a shared cache that an optimistic add-merge can
    // briefly double-count. XHR bypasses it so progress matches the real cart.
    const token = (this._cartReadToken = (this._cartReadToken || 0) + 1);
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${window.Shopify?.routes?.root || '/'}cart.js`, true);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.onload = () => {
      if (token !== this._cartReadToken || !this.isConnected) return;
      if (xhr.status < 200 || xhr.status >= 300) return;
      let freshCart;
      try {
        freshCart = JSON.parse(xhr.responseText);
      } catch {
        return;
      }
      this._trackDeclinedAutoGwps(this.lastCartItems, freshCart.items || [], requestState);
      this.lastCartItems = freshCart.items || [];
      this.render(freshCart);
    };
    xhr.send();
  }

  getRegionData() {
    const entries = window.tiered_gift_entries || [];
    const settings = window.settings || {};
    const currency = this.currency;

    const freeShippingMap = {
      USD: { enabled: settings.cart_free_shipping_enable, amount: settings.cart_free_shipping_amount },
      CAD: { enabled: settings.cart_free_shipping_enable_cad, amount: settings.cart_free_shipping_amount_cad },
      GBP: { enabled: settings.cart_free_shipping_enable_gbp, amount: settings.cart_free_shipping_amount_gbp },
      EUR: { enabled: settings.cart_free_shipping_enable_eur, amount: settings.cart_free_shipping_amount_eur },
      BGN: { enabled: settings.cart_free_shipping_enable_bgn, amount: settings.cart_free_shipping_amount_bgn },
      CZK: { enabled: settings.cart_free_shipping_enable_czk, amount: settings.cart_free_shipping_amount_czk },
      DKK: { enabled: settings.cart_free_shipping_enable_dkk, amount: settings.cart_free_shipping_amount_dkk },
      HUF: { enabled: settings.cart_free_shipping_enable_huf, amount: settings.cart_free_shipping_amount_huf },
      PLN: { enabled: settings.cart_free_shipping_enable_pln, amount: settings.cart_free_shipping_amount_pln },
      RON: { enabled: settings.cart_free_shipping_enable_ron, amount: settings.cart_free_shipping_amount_ron },
      SEK: { enabled: settings.cart_free_shipping_enable_sek, amount: settings.cart_free_shipping_amount_sek },
      AUD: { enabled: settings.cart_free_shipping_enable_aud, amount: settings.cart_free_shipping_amount_aud },
      NZD: { enabled: settings.cart_free_shipping_enable_nzd, amount: settings.cart_free_shipping_amount_nzd },
    };

    const fsCfg = freeShippingMap[currency] || freeShippingMap.USD;
    const regionData = {
      freeShipping: { enabled: fsCfg.enabled, amount: fsCfg.amount },
      tieredGWP: [],
      tieredGWPCartDrawer: [],
    };

    entries.forEach((entry) => {
      (entry.regionSpecificThresholds || []).forEach((rt) => {
        const [region, amount] = rt.split('-');
        if (region !== currency) return;

        const giftData = {
          gift_title: entry.gift_title,
          product_references: entry.product_references || [],
          variant_references: entry.variant_references || [],
          threshold: parseFloat(amount),
        };

        if (
          (!giftData.product_references || giftData.product_references.length === 0) &&
          giftData.variant_references.length > 0
        ) {
          const firstProductId = giftData.variant_references[0].product_id;
          const filtered = giftData.variant_references.filter(
            (v) => v.product_id === firstProductId
          );
          giftData.product_references = filtered.map((v) => ({
            id: v.product_id,
            title: v.product_title,
            image: v.image,
            _fromVariantReference: true,
            _threshold: giftData.threshold,
            variants: [
              {
                id: v.id,
                title: v.title,
                available: v.available,
                image: v.image,
                swatch: v.swatch,
                description: v.description,
              },
            ],
          }));
        }

        regionData.tieredGWP.push(giftData);

        const giftDataCartDrawer = {
          gift_title: giftData.gift_title,
          product_references: [],
          variant_references: entry.variant_references || [],
          threshold: parseFloat(giftData.threshold),
        };

        if (entry.product_references && entry.product_references.length > 0) {
          giftDataCartDrawer.product_references = entry.product_references;
        } else if (entry.variant_references && entry.variant_references.length > 0) {
          const groupedByProduct = {};
          entry.variant_references.forEach((v) => {
            if (!groupedByProduct[v.product_id]) {
              groupedByProduct[v.product_id] = {
                id: v.product_id,
                title: v.product_title,
                image: v.image,
                variants: [],
              };
            }
            groupedByProduct[v.product_id].variants.push({
              id: v.id,
              title: v.title,
              available: v.available,
              image: v.image,
              swatch: v.swatch,
            });
          });
          giftDataCartDrawer.product_references = Object.values(groupedByProduct);
        }

        regionData.tieredGWPCartDrawer.push(giftDataCartDrawer);
      });
    });

    return regionData;
  }

  buildThresholds(regionData) {
    const thresholds = [];

    if (regionData.freeShipping?.enabled && regionData.freeShipping.amount) {
      thresholds.push({
        type: 'shipping',
        title: null,
        amount: parseFloat(regionData.freeShipping.amount),
        product_references: [],
        variant_references: [],
      });
    }

    if (Array.isArray(regionData.tieredGWP)) {
      for (const gift of regionData.tieredGWP) {
        thresholds.push({
          type: 'gwp',
          title: gift.gift_title,
          amount: parseFloat(gift.threshold),
          product_references: gift.product_references || [],
          variant_references: gift.variant_references || [],
        });
      }
    }

    thresholds.sort((a, b) => a.amount - b.amount);
    return thresholds;
  }

  buildGwpVariantMap(regionData, cartItems) {
    this.gwpVariantMap = {};

    (regionData.tieredGWPCartDrawer || []).forEach((gift) => {
      (gift.product_references || []).forEach((product) => {
        const cartItem = cartItems.find(
          (item) => item.product_id === product.id && item.properties?._gwp
        );
        const lineItemKey = cartItem ? cartItem.key : null;
        this.gwpVariantMap[product.id] = this._buildGwpVariantsHtml(product, lineItemKey);
      });
    });
  }

  _buildGwpVariantsHtml(product, lineItemKey) {
    const hasSingleVariant = (product.variants || []).length === 1;
    if (hasSingleVariant) return '';

    const liHtml = (product.variants || [])
      .map((variant) => {
        const isAvailable = variant.available;
        return `
          <li>
            <span class="gwp-line-item-swatch ${isAvailable ? '' : 'is-unavailable'}">
              <button class="gwp-product-variant-button ${isAvailable ? '' : 'is-unavailable'}" data-variant-id="${variant.id}" aria-label="${variant.title}">
                <span class="visually-hidden">${variant.title}</span>
                <i style="background: ${variant.swatch || '#ccc'}"></i>
              </button>
            </span>
          </li>
        `;
      })
      .join('');

    return `
      <button class="line-item-change-variant">Change Shade</button>
      <ul style="display: none;" class="gwp-line-item-variants" data-cart-key="${lineItemKey}">${liHtml}</ul>
    `;
  }

  render(cart) {
    // Resolve the live messages bar fresh each render. It's a preserved static
    // element that liquid-ajax-cart moves during section swaps, so a reference
    // cached in connectedCallback can be stale or null mid-swap. Re-querying
    // keeps the bar updating in place rather than blinking out.
    const messagesEl = document.getElementById('tiered-gwp-messages') || this.messagesEl;
    this.messagesEl = messagesEl;

    const regionData = this.getRegionData();
    const thresholds = this.buildThresholds(regionData);

    if (!thresholds.length) {
      this.hidden = true;
      if (messagesEl) messagesEl.innerHTML = '';
      return;
    }

    const totalPrice = (cart.total_price || 0) / 100;
    const cartItems = cart.items || [];

    this.buildGwpVariantMap(regionData, cartItems);

    if (!this.isBFCM && messagesEl) {
      const lastAmount = thresholds[thresholds.length - 1].amount;
      const allUnlocked = totalPrice >= lastAmount;
      const shippingOnly = thresholds.every((t) => t.type === 'shipping');
      const { messages } = this.getProgressMessages(regionData, thresholds, totalPrice);

      let messagesHtml = '';
      if (allUnlocked && shippingOnly) {
        messagesHtml = `<div class="single--message message--shipping-unlocked">${this.constructor.SHIPPING_UNLOCKED_ICON}<span>You've unlocked <strong>Free Shipping</strong></span></div>`;
      } else if (messages.length === 1) {
        messagesHtml = `<div class="single--message">${messages[0]}</div>`;
      } else {
        messagesHtml = messages
          .map(
            (msg, i) =>
              `<div class="${i === 0 ? 'message--unlocked' : 'message--locked'}">${msg}</div>`
          )
          .join('');
      }

      const progressBarHtml = allUnlocked ? '' : this.renderProgressBar(thresholds, totalPrice);
      const newHtml = messagesHtml + (progressBarHtml || '');
      // Only rewrite when the content actually changed — avoids destroying and
      // recreating the progress bar (and its repaint) on redundant renders.
      if (messagesEl.innerHTML !== newHtml) messagesEl.innerHTML = newHtml;
    }

    if (this.productsEl) {
      const unlockedHtml = this.renderUnlockedProducts(thresholds, totalPrice, cartItems);
      if (typeof unlockedHtml === 'string') {
        this.productsEl.innerHTML = unlockedHtml;
      }
    }

    this.injectGwpVariantButtons(cartItems);
    this.hidden = false;

    if ((cart.item_count || cartItems.length) > 0) {
      document.documentElement.classList.remove('js-ajax-cart-empty');
      document.documentElement.classList.add('js-ajax-cart-not-empty');
    }
  }

  getProgressMessages(regionData, thresholds, totalPrice) {
    if (!thresholds.length) return { messages: [] };

    // If we've hit or passed the last threshold, show one final unlocked message.
    // Matches JonesRoadBeauty-Main 8/cartTemplate.js getProgressMessages exactly.
    const lastAmount = thresholds[thresholds.length - 1].amount;
    if (totalPrice >= lastAmount) {
      const giftCount = thresholds.filter((t) => t.type === 'gwp').length;
      const hasShipping = thresholds.some(
        (t) => t.type === 'shipping' && totalPrice >= t.amount
      );
      const parts = [];
      if (hasShipping) parts.push('<strong>Free Shipping</strong>');
      if (giftCount > 0) parts.push(`${giftCount} Free Gift${giftCount > 1 ? 's' : ''}`);
      if (parts.length > 0) {
        return { messages: [`You've unlocked ${parts.join(' + ')}`] };
      }
    }

    const formatDiff = (num) => {
      const cents = Math.max(0, Math.round(num * 100));
      return this.formatMoney(cents);
    };

    const messages = [];

    // Find last unlocked threshold (<= totalPrice)
    let lastUnlockedIdx = -1;
    for (let i = thresholds.length - 1; i >= 0; i--) {
      if (totalPrice >= thresholds[i].amount) {
        lastUnlockedIdx = i;
        break;
      }
    }

    // Top message focuses on the NEXT locked tier.
    // When free shipping is already unlocked and the next goal is a gift,
    // prepend a confirmation so customers know shipping is in hand.
    const nextLocked = thresholds.find((t) => t.amount > totalPrice);
    if (nextLocked) {
      const diff = formatDiff(nextLocked.amount - totalPrice);
      const nextLabel =
        nextLocked.type === 'shipping'
          ? '<strong>Free Shipping</strong>'
          : `a <strong>Free ${nextLocked.title.replace(/^(The|Free)\s+/i, '')}</strong>`;
      const shippingUnlocked = thresholds.some(
        (t) => t.type === 'shipping' && totalPrice >= t.amount
      );
      if (shippingUnlocked && nextLocked.type !== 'shipping') {
        messages.push(
          `You have <strong>Free Shipping</strong>. Add <strong>${diff}</strong> to unlock ${nextLabel}`
        );
      } else {
        messages.push(`You're just <strong>${diff}</strong> away from ${nextLabel}`);
      }
    } else if (lastUnlockedIdx >= 0) {
      // All thresholds unlocked
      const giftCount = thresholds.filter((t) => t.type === 'gwp').length;
      const hasShipping = thresholds.some((t) => t.type === 'shipping');
      const parts = [];
      if (hasShipping) parts.push('Free Shipping');
      if (giftCount > 0) parts.push(`${giftCount} Free Gift${giftCount > 1 ? 's' : ''}`);
      messages.push(`You've unlocked ${parts.join(' + ')}`);
    } else {
      // None unlocked yet -> show the first goal only
      const first = thresholds[0];
      const diff = formatDiff(first.amount - totalPrice);
      const nextLabel =
        first.type === 'shipping'
          ? '<strong>Free Shipping</strong>'
          : `a <strong>Free ${first.title.replace(/^(The|Free)\s+/i, '')}</strong>`;
      messages.push(`You're just <strong>${diff}</strong> away from ${nextLabel}`);
    }

    return { messages };
  }

  renderProgressBar(thresholds, totalPrice) {
    if (!thresholds?.length) return '';

    const numThresholds = thresholds.length;
    const segmentWidthPercent = 100 / numThresholds;
    let cumulativeFill = 0;

    for (let i = 0; i < numThresholds; i++) {
      const prevAmount = i === 0 ? 0 : thresholds[i - 1].amount;
      const currAmount = thresholds[i].amount;

      if (totalPrice >= currAmount) {
        cumulativeFill += segmentWidthPercent;
      } else if (totalPrice > prevAmount) {
        const partialRatio = (totalPrice - prevAmount) / (currAmount - prevAmount);
        cumulativeFill += partialRatio * segmentWidthPercent;
        break;
      } else {
        break;
      }
    }

    const dotsHTML = thresholds
      .map((threshold, idx) => {
        const left = Math.min((idx + 1) * segmentWidthPercent, 100);
        const isActive = totalPrice >= threshold.amount ? 'active' : '';
        const isLast = idx === thresholds.length - 1;
        const offsetValueStyle = isLast
          ? 'transform: translateX(-100%); text-align: right'
          : 'transform: translateX(-50%)';
        const offsetLabelStyle = isLast
          ? 'transform: translateX(-100%); text-align: right'
          : 'transform: translateX(-50%)';
        const offsetDotStyle = isLast ? 'transform: translate(-70%, -50%)' : '';
        const label =
          threshold.type === 'shipping' ? 'Free Shipping' : threshold.title;

        return `
          <div class="threshold-dot ${isActive}" style="left:${left}%; ${offsetDotStyle}"></div>
          <div class="threshold-value" style="left:${left}%; ${offsetValueStyle}">${this.formatMoney(Math.round(threshold.amount * 100))}</div>
          <div class="threshold-label" style="left:${left}%; ${offsetLabelStyle}">${label}</div>
        `;
      })
      .join('');

    return `
      <div class="progress-bar-wrapper ${numThresholds > 1 ? 'progress-bar-wrapper-multiple' : 'progress-bar-wrapper-single'}">
        <div class="progress-bar-bg"></div>
        <div class="progress-bar-fill" style="width:${cumulativeFill}%;"></div>
        ${dotsHTML}
      </div>
    `;
  }

  renderUnlockedProducts(thresholds, totalPrice, cartItems) {
    if (!Array.isArray(thresholds) || !Array.isArray(cartItems)) return '';

    const unlockedProducts = [];
    const removeLineKeys = [];

    const validGwpIds = thresholds
      .filter((t) => t.type === 'gwp')
      .flatMap((t) => (t.product_references || []).map((ref) => ref.id));

    thresholds.forEach((threshold) => {
      const qualifies = threshold.type === 'gwp' && totalPrice >= threshold.amount;
      const productRefs = threshold.product_references || [];

      const gwpItemsInCart = cartItems.filter(
        (item) =>
          item.properties?._gwp &&
          productRefs.some((ref) => ref.id == item.product_id)
      );

      if (qualifies) {
        if (gwpItemsInCart.length === 0) {
          productRefs.forEach((productRef) => {
            unlockedProducts.push({
              thresholdTitle: threshold.title || 'Gift',
              product: productRef,
            });
          });
        }
      } else {
        gwpItemsInCart.forEach((item) => {
          removeLineKeys.push(item.key);
        });
      }
    });

    let autoGwpVariantId = null;
    try {
      const gwpData = JSON.parse(document.body.dataset.gwp || '{}');
      if (gwpData.enabled && gwpData.product) autoGwpVariantId = gwpData.product;
    } catch (e) {
      /* ignore */
    }

    cartItems.forEach((item) => {
      if (
        (item.properties?._gwp_item || item.properties?._gwp) &&
        autoGwpVariantId &&
        item.variant_id == autoGwpVariantId
      ) {
        return;
      }
      if (item.properties?._gwp && !validGwpIds.includes(item.product_id)) {
        removeLineKeys.push(item.key);
      }
    });

    if (removeLineKeys.length && !CartTieredGwp._processing) {
      CartTieredGwp._processing = true;
      const cartAfterRemove = cartItems.filter(
        (item) => !removeLineKeys.includes(item.key)
      );
      const pendingAutoAddItems = this._getAutoAddItems(
        thresholds,
        totalPrice,
        cartAfterRemove
      );
      const updates = {};
      removeLineKeys.forEach((key) => {
        updates[key] = 0;
      });

      if (window.liquidAjaxCart?.update) {
        window.liquidAjaxCart.update(
          { updates },
          {
            info: { initiator: 'cart-tiered-gwp' },
            lastCallback: () => {
              CartTieredGwp._finishRemoveRequest(pendingAutoAddItems);
            },
          }
        );
      } else {
        fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        })
          .then(() => {
            CartTieredGwp._finishRemoveRequest(pendingAutoAddItems);
          })
          .catch(() => {
            CartTieredGwp._finishRemoveRequest([]);
          });
      }
      return '';
    }

    const autoAddItems = this._getAutoAddItems(thresholds, totalPrice, cartItems);
    if (autoAddItems.length && !CartTieredGwp._processing) {
      CartTieredGwp._executeAutoAdd(autoAddItems);
      return '';
    }

    const shippingThreshold = thresholds.find((t) => t.type === 'shipping');
    if (shippingThreshold && totalPrice < shippingThreshold.amount) {
      return '<div class="unlocked-gwp-products"></div>';
    }

    const nextThreshold = thresholds
      .filter((t) => t.type === 'gwp' && t.amount > totalPrice)
      .sort((a, b) => a.amount - b.amount)[0];

    const teaserHtml = nextThreshold ? this.buildGwpTeaserBlock(nextThreshold) : '';

    const html = `
      <div class="unlocked-gwp-products">
        ${thresholds
          .filter((t) => t.type === 'gwp' && totalPrice >= t.amount)
          .map((threshold) => {
            const productRefs = unlockedProducts.filter(
              (p) =>
                !p.product._fromVariantReference &&
                p.thresholdTitle === (threshold.title || 'Gift')
            );

            const variantRefs = unlockedProducts
              .filter(
                (p) =>
                  p.product._fromVariantReference &&
                  p.thresholdTitle === (threshold.title || 'Gift')
              )
              .map((p, idx) => ({
                ...p,
                _refIndex: idx,
                _threshold: p.product._threshold,
              }));

            const groupedVariantRefs = variantRefs.reduce((acc, ref) => {
              if (!acc[ref._threshold]) acc[ref._threshold] = [];
              acc[ref._threshold].push(ref);
              return acc;
            }, {});

            const hasUnlocked = productRefs.length > 0 || variantRefs.length > 0;

            return hasUnlocked
              ? `
                <div class="gwp-threshold-block">
                  <div class="gwp-header">
                    <div class="gwp-header-title">Add Free ${
                      threshold.title.replace(/^The\s+/i, '') || 'Gift'
                    } Below</div>
                    ${
                      (productRefs.length > 0 &&
                        productRefs[0].product.variants.length > 1) ||
                      variantRefs.length > 1
                        ? '<div class="gwp-header-subtitle">Choose your shade below</div>'
                        : ''
                    }
                  </div>

                  ${productRefs
                    .map(({ product }) =>
                      this.buildGwpProductBlock(product, { isVariantRef: false })
                    )
                    .join('')}

                  ${Object.entries(groupedVariantRefs)
                    .map(
                      ([th, refs]) => `
                        <div class="variant-reference-wrapper" data-threshold="${th}">
                          ${refs
                            .map(({ product, _refIndex }) =>
                              this.buildGwpProductBlock(product, {
                                isVariantRef: true,
                                idx: _refIndex,
                              })
                            )
                            .join('')}
                        </div>
                      `
                    )
                    .join('')}
                </div>
              `
              : '';
          })
          .join('')}

        ${teaserHtml}
      </div>
    `;

    return html;
  }

  buildGwpProductBlock(
    product,
    { isVariantRef = false, idx = 0, disabled = false } = {}
  ) {
    const imageHtml = product.image
      ? `<img src="${product.image}" alt="${product.title}" />`
      : '';

    const titleHtml = `
      <div class="title-wrapper">
        <div class="gwp-product-title">${
          isVariantRef ? product.variants[0].title : product.title
        }</div>
        <div class="free-gift-title">FREE</div>
        ${
          isVariantRef && product.variants[0]?.description
            ? `<div class="gwp-product-description">${product.variants[0].description}</div>`
            : ''
        }
      </div>
      <div class="variant-title"></div>
    `;

    const hasSingleVariant = (product.variants || []).length === 1;
    const firstVariant = (product.variants || [])[0];
    const inputName = isVariantRef
      ? `gwp-variant-${product.id}-${idx}`
      : `gwp-${product.id}`;

    const variantsHtml = (product.variants || [])
      .map((variant, vidx) => {
        const isChecked = hasSingleVariant && vidx === 0 ? 'checked' : '';
        const isAvailable = variant.available;
        const isSelectedClass =
          hasSingleVariant && isAvailable && vidx === 0 ? 'selected' : '';

        return `
          <li>
            <span class="swatch ${isAvailable ? '' : 'is-unavailable'}">
              <input type="radio"
                name="${inputName}"
                id="variant-${variant.id}"
                value="${variant.id}"
                data-name="${variant.title}"
                data-image="${variant.image || ''}"
                class="visually-hidden"
                ${isChecked}>
              <label for="variant-${variant.id}" aria-label="${variant.title}" class="${isSelectedClass} ${isAvailable ? '' : 'is-unavailable'}">
                <span class="visually-hidden">${variant.title}</span>
                <i style="background: ${variant.swatch || '#ccc'}"></i>
              </label>
            </span>
          </li>
        `;
      })
      .join('');

    let buttonLabel, buttonDisabled;
    if (disabled) {
      buttonLabel = 'Add to Cart';
      buttonDisabled = 'disabled';
    } else if (hasSingleVariant) {
      if (firstVariant.available) {
        buttonLabel = 'Add to Cart';
        buttonDisabled = '';
      } else {
        buttonLabel = 'Sold Out';
        buttonDisabled = 'disabled';
      }
    } else {
      buttonLabel = 'Select a Shade';
      buttonDisabled = 'disabled';
    }

    const variantsClass = hasSingleVariant
      ? 'gwp-product-variants single-variant-only'
      : 'gwp-product-variants';

    return `
      <div class="gwp-product-block ${disabled ? 'disabled' : ''}">
        <div class="gwp-product-image">${imageHtml}</div>
        <div class="gwp-product-info">
          ${titleHtml}
          <div class="gwp-form">
            <ul class="${variantsClass}">${variantsHtml}</ul>
            <button type="button" class="gwp-product-button" ${buttonDisabled}>${buttonLabel}</button>
          </div>
        </div>
      </div>
    `;
  }

  buildGwpTeaserBlock(threshold) {
    const { title, amount, product_references = [] } = threshold;

    const productRefs = product_references.filter((p) => !p._fromVariantReference);

    const variantRefs = product_references
      .filter((p) => p._fromVariantReference)
      .map((p, idx) => ({
        product: {
          id: p.id,
          title: p.title,
          image: p.image,
          variants: p.variants || [],
          _fromVariantReference: true,
          _threshold: amount,
        },
        _refIndex: idx,
      }));

    const groupedVariantRefs = variantRefs.reduce((acc, ref) => {
      if (!acc[ref.product._threshold]) acc[ref.product._threshold] = [];
      acc[ref.product._threshold].push(ref);
      return acc;
    }, {});

    const hasTeasers = productRefs.length > 0 || variantRefs.length > 0;

    return `
      <div class="teaser-gwp-products">
        ${
          hasTeasers
            ? `
              <div class="gwp-header">
                <div class="gwp-header-title">
                  Free ${title.replace(/^The\s+/i, '')} on all orders over <em>${this.formatMoney(Math.round(amount * 100))}</em>
                </div>
              </div>
            `
            : ''
        }

        ${productRefs
          .map((product) =>
            this.buildGwpProductBlock(
              { ...product, _fromVariantReference: false, _threshold: amount },
              { isVariantRef: false, disabled: true }
            )
          )
          .join('')}

        ${Object.entries(groupedVariantRefs)
          .map(
            ([th, refs]) => `
              <div class="variant-reference-wrapper teaser" data-threshold="${th}">
                ${refs
                  .map(({ product, _refIndex }) =>
                    this.buildGwpProductBlock(product, {
                      isVariantRef: true,
                      idx: _refIndex,
                      disabled: true,
                    })
                  )
                  .join('')}
              </div>
            `
          )
          .join('')}
      </div>
    `;
  }

  _isOwnCartRequest(requestState) {
    return requestState?.info?.initiator === 'cart-tiered-gwp';
  }

  _trackDeclinedAutoGwps(prevItems, nextItems, requestState) {
    if (!this._isTieredGwpAutoAddEnabled() || this._isOwnCartRequest(requestState)) return;

    const next = nextItems || [];
    const totalPrice = next.reduce(
      (sum, item) => sum + (item.final_line_price || item.line_price || 0),
      0
    ) / 100;
    const thresholds = this.buildThresholds(this.getRegionData());

    (prevItems || []).forEach((item) => {
      if (!item.properties?._gwp_auto) return;
      const stillInCart = next.some(
        (line) => line.product_id === item.product_id && line.properties?._gwp
      );
      if (stillInCart) return;

      thresholds.forEach((threshold) => {
        if (threshold.type !== 'gwp' || totalPrice < threshold.amount) return;
        const inTier = (threshold.product_references || []).some(
          (ref) => ref.id == item.product_id
        );
        if (inTier) {
          CartTieredGwp._declinedAutoGwpKeys.add(
            CartTieredGwp._declineKey(threshold.amount, item.product_id)
          );
        }
      });
    });
  }

  _clearDeclinedAutoGwpsForInactiveTiers(thresholds, totalPrice) {
    thresholds.forEach((threshold) => {
      if (threshold.type !== 'gwp' || totalPrice >= threshold.amount) return;
      (threshold.product_references || []).forEach((ref) => {
        CartTieredGwp._declinedAutoGwpKeys.delete(
          CartTieredGwp._declineKey(threshold.amount, ref.id)
        );
      });
    });
  }

  _isDeclinedAutoGwp(thresholdAmount, productId) {
    return CartTieredGwp._declinedAutoGwpKeys.has(
      CartTieredGwp._declineKey(thresholdAmount, productId)
    );
  }

  _isTieredGwpAutoAddEnabled() {
    return !!window.settings?.cart_tiered_gwp_auto_add;
  }

  _getGwpProperties(autoAdd = false) {
    const properties = { _gwp: true };
    const settings = window.settings || {};
    if (
      settings.enable_test_promotion &&
      settings.test_promotion_property &&
      settings.test_promotion_property_value
    ) {
      properties[settings.test_promotion_property] =
        settings.test_promotion_property_value;
    }
    if (autoAdd) properties._gwp_auto = true;
    return properties;
  }

  _getAutoAddItems(thresholds, totalPrice, cartItems) {
    if (!this._isTieredGwpAutoAddEnabled()) return [];

    this._clearDeclinedAutoGwpsForInactiveTiers(thresholds, totalPrice);

    const items = [];

    thresholds.forEach((threshold) => {
      if (threshold.type !== 'gwp' || totalPrice < threshold.amount) return;

      const productRefs = threshold.product_references || [];
      const gwpItemsInCart = cartItems.filter(
        (item) =>
          item.properties?._gwp &&
          productRefs.some((ref) => ref.id == item.product_id)
      );
      if (gwpItemsInCart.length > 0) return;

      const regularProducts = productRefs.filter((p) => !p._fromVariantReference);
      const variantRefProducts = productRefs.filter((p) => p._fromVariantReference);

      regularProducts.forEach((product) => {
        if (this._isDeclinedAutoGwp(threshold.amount, product.id)) return;
        const variants = product.variants || [];
        if (variants.length !== 1 || !variants[0].available) return;
        items.push({
          id: variants[0].id,
          quantity: 1,
          properties: this._getGwpProperties(true),
        });
      });

      if (variantRefProducts.length === 1) {
        const product = variantRefProducts[0];
        if (this._isDeclinedAutoGwp(threshold.amount, product.id)) return;
        const variants = product.variants || [];
        if (variants.length === 1 && variants[0].available) {
          items.push({
            id: variants[0].id,
            quantity: 1,
            properties: this._getGwpProperties(true),
          });
        }
      }
    });

    return items;
  }

  onChange(e) {
    const input = e.target.closest('input[type="radio"]');
    if (!input) return;
    const block = input.closest('.gwp-product-block');
    if (!block) return;

    block.querySelectorAll('label').forEach((l) => l.classList.remove('selected'));
    const label = block.querySelector(`label[for="${input.id}"]`);
    if (label) label.classList.add('selected');

    const imgEl = block.querySelector('.gwp-product-image img');
    if (imgEl && input.dataset.image) imgEl.src = input.dataset.image;

    const variantTitle = block.querySelector('.variant-title');
    if (variantTitle) variantTitle.textContent = input.dataset.name;

    const btn = block.querySelector('.gwp-product-button');
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Add to Cart';
    }
  }

  async onClick(e) {
    const btn = e.target.closest('.gwp-product-button');
    if (!btn) return;

    const block = btn.closest('.gwp-product-block');
    const selectedInput = block?.querySelector('input[type="radio"]:checked');
    if (!selectedInput) return;

    const variantId = parseInt(selectedInput.value);
    if (!variantId) return;

    const properties = this._getGwpProperties();

    document
      .querySelectorAll('.gwp-product-button')
      .forEach((b) => (b.disabled = true));
    btn.textContent = 'Adding...';

    if (window.liquidAjaxCart?.add) {
      window.liquidAjaxCart.add(
        { items: [{ id: variantId, quantity: 1, properties }] },
        {
          info: { initiator: 'cart-tiered-gwp' },
          lastCallback: () => {
            this._restoreGwpButtonStates();
          },
        }
      );
    } else {
      try {
        await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1, properties }],
          }),
        });
        window.location.reload();
      } catch (err) {
        console.error('GWP add error:', err);
      } finally {
        this._restoreGwpButtonStates();
      }
    }
  }

  _restoreGwpButtonStates() {
    document.querySelectorAll('.gwp-product-button').forEach((b) => {
      const block = b.closest('.gwp-product-block');
      if (!block) return;

      // Teaser blocks should always remain disabled.
      if (block.classList.contains('disabled')) {
        b.disabled = true;
        b.textContent = 'Add to Cart';
        return;
      }

      const radios = block.querySelectorAll('input[type="radio"]');
      const hasChecked = !!block.querySelector('input[type="radio"]:checked');
      const isSingleVariant = radios.length === 1;

      if (hasChecked || isSingleVariant) {
        b.disabled = false;
        b.textContent = 'Add to Cart';
      } else {
        b.disabled = true;
        b.textContent = 'Select a Shade';
      }
    });
  }

  injectGwpVariantButtons(cartItems) {
    cartItems.forEach((item) => {
      if (!item.properties?._gwp) return;
      const variantsHtml = this.gwpVariantMap[item.product_id];
      if (!variantsHtml) return;

      const cartItemEls = document.querySelectorAll('.cart-item--gwp');
      cartItemEls.forEach((el) => {
        const elKey = el.dataset.lineItemKey;
        if (!elKey || elKey !== item.key) return;

        if (el.querySelector('.line-item-change-variant')) return;

        const gwpActions = el.querySelector('.cart-item__gwp-actions');
        if (gwpActions) {
          gwpActions.insertAdjacentHTML('beforebegin', variantsHtml);
        }
      });
    });
  }

  _applyGwpShadeSwapOptimisticUI(variantBtn, ul) {
    const cartItem = ul.closest('.cart-item--gwp');
    if (!cartItem) return;

    cartItem.classList.add('is-gwp-shade-swapping');

    const changeBtn = ul.previousElementSibling;
    if (changeBtn?.matches('.line-item-change-variant')) {
      changeBtn.style.display = 'block';
      changeBtn.textContent = 'Updating...';
      changeBtn.disabled = true;
    }

    ul.dataset.prevDisplay = ul.style.display || 'none';
    ul.style.display = 'none';
    ul.querySelectorAll('.gwp-product-variant-button').forEach((btn) => {
      btn.disabled = true;
    });

    const swatch = variantBtn.closest('.gwp-line-item-swatch');
    ul.querySelectorAll('.gwp-line-item-swatch').forEach((s) =>
      s.classList.remove('is-selected')
    );
    if (swatch) swatch.classList.add('is-selected');

    const shadeTitle = variantBtn.getAttribute('aria-label');
    const variantInfo = cartItem.querySelector('.cart-item__variant-info');
    if (variantInfo && shadeTitle) variantInfo.textContent = shadeTitle;
  }

  _clearGwpShadeSwapOptimisticUI(cartItem) {
    if (!cartItem) return;

    cartItem.classList.remove('is-gwp-shade-swapping');

    const changeBtn = cartItem.querySelector('.line-item-change-variant');
    if (changeBtn) {
      changeBtn.textContent = 'Change Shade';
      changeBtn.disabled = false;
      changeBtn.style.display = 'block';
    }

    const ul = cartItem.querySelector('.gwp-line-item-variants');
    if (ul) {
      ul.style.display = ul.dataset.prevDisplay || 'none';
      delete ul.dataset.prevDisplay;
    }

    cartItem.querySelectorAll('.gwp-product-variant-button').forEach((btn) => {
      btn.disabled = false;
    });
  }

  _finishGwpShadeSwap() {
    CartTieredGwp._swapInProgress = false;

    const el = document.querySelector('cart-tiered-gwp');
    if (!el?.isConnected) return;

    const cached = window.__cartJsCache?.get?.();
    if (cached && typeof cached.total_price !== 'undefined') {
      el.lastCartItems = cached.items || [];
      el.render(cached);
      return;
    }

    const root = window.Shopify?.routes?.root || '/';
    fetch(`${root}cart.js`)
      .then((r) => r.json())
      .then((cart) => {
        if (!el.isConnected) return;
        el.lastCartItems = cart.items || [];
        el.render(cart);
      })
      .catch(() => {});
  }

  _handleLineItemChangeVariant(e) {
    if (CartTieredGwp._swapInProgress) return;
    if (!e.target.matches('.line-item-change-variant')) return;
    const ul = e.target.nextElementSibling;
    if (!ul) return;
    ul.style.display = ul.style.display === 'flex' ? 'none' : 'flex';
    e.target.style.display = e.target.style.display === 'none' ? 'block' : 'none';
  }

  _handleLineItemVariantClick(e) {
    if (CartTieredGwp._swapInProgress) return;

    const variantBtn = e.target.closest('.gwp-product-variant-button');
    if (!variantBtn || variantBtn.disabled) return;
    if (variantBtn.classList.contains('is-unavailable')) return;

    const li = variantBtn.closest('li');
    const ul = li?.closest('.gwp-line-item-variants');
    if (!ul) return;

    const variantId = parseInt(variantBtn.dataset.variantId);
    const oldKey = ul.dataset.cartKey;
    if (!variantId || !oldKey) return;

    const cartItem = ul.closest('.cart-item--gwp');
    const properties = this._getGwpProperties();

    CartTieredGwp._swapInProgress = true;
    this._applyGwpShadeSwapOptimisticUI(variantBtn, ul);

    const root = window.Shopify?.routes?.root || '/';

    fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: { [oldKey]: 0 } }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Remove failed');

        if (window.liquidAjaxCart?.add) {
          window.liquidAjaxCart.add(
            { items: [{ id: variantId, quantity: 1, properties }] },
            {
              info: { initiator: 'cart-tiered-gwp' },
              lastCallback: () => {
                this._finishGwpShadeSwap();
              },
            }
          );
        } else {
          return fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1, properties }],
            }),
          }).then(() => {
            CartTieredGwp._swapInProgress = false;
            window.location.reload();
          });
        }
      })
      .catch((err) => {
        CartTieredGwp._swapInProgress = false;
        this._clearGwpShadeSwapOptimisticUI(cartItem);
        console.error('Failed to update GWP variant:', err);
      });
  }

}

if (!customElements.get('cart-tiered-gwp')) {
  customElements.define('cart-tiered-gwp', CartTieredGwp);
}
